import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Button,
  Chip,
  Grid,
  TextField,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  Email as EmailIcon,
  Chat as ChatIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  AccountBalance as BudgetIcon,
  Receipt as TransactionIcon,
  Security as SecurityIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { support } from '../services/api';

interface FAQItem {
  id: string;
  category: string;
  question: string;
  answer: string;
  tags: string[];
}

const Support: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: '',
    email: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const faqData: FAQItem[] = [
    // Getting Started
    {
      id: '1',
      category: 'Getting Started',
      question: 'How do I create my first budget?',
      answer: 'To create your first budget: 1) Navigate to the Budgets page from the sidebar, 2) Click the "New Budget" button, 3) Enter your budget name and select the month/year, 4) Add budget categories with their allocated amounts, 5) Save your budget. Your budget will track your spending against these allocated amounts.',
      tags: ['budget', 'create', 'new', 'first time']
    },
    {
      id: '2',
      category: 'Getting Started',
      question: 'How do I add my first transaction?',
      answer: 'To add a transaction: 1) Go to the Transactions page, 2) Click "Add Transaction", 3) Select whether it\'s an income or expense, 4) Choose the appropriate category, 5) Enter the amount and description, 6) Select the date, 7) Save the transaction. The transaction will automatically update your budget tracking.',
      tags: ['transaction', 'add', 'income', 'expense', 'new']
    },
    {
      id: '3',
      category: 'Getting Started',
      question: 'What should I do after creating my account?',
      answer: 'After creating your account: 1) Verify your email if prompted, 2) Set up your first budget for the current month, 3) Add your income sources, 4) Start tracking your expenses by adding transactions, 5) Review your dashboard to see your financial overview. Consider exploring the Settings page to customize your experience.',
      tags: ['account', 'setup', 'first steps', 'verification']
    },

    // Budget Management
    {
      id: '4',
      category: 'Budget Management',
      question: 'How do I edit or delete a budget?',
      answer: 'To edit a budget: Click on the budget card and select "Edit" to modify categories and amounts. To delete a budget: Click the delete icon on the budget card and confirm the action. Note: Deleting a budget will not delete associated transactions, but they will no longer be categorized under that budget.',
      tags: ['budget', 'edit', 'delete', 'modify', 'remove']
    },
    {
      id: '5',
      category: 'Budget Management',
      question: 'Can I copy a budget from a previous month?',
      answer: 'Yes! When creating a new budget, you can use a previous month\'s budget as a template. Click "New Budget", then select "Copy from previous month" option. This will pre-populate the categories and amounts from your selected previous budget, which you can then modify as needed.',
      tags: ['budget', 'copy', 'template', 'previous month', 'duplicate']
    },
    {
      id: '6',
      category: 'Budget Management',
      question: 'How do budget categories work?',
      answer: 'Budget categories help organize your spending. Each category has an allocated amount that represents how much you plan to spend. As you add transactions to that category, the app tracks how much you\'ve spent versus your budget. Categories can include things like groceries, utilities, entertainment, etc.',
      tags: ['categories', 'budget', 'allocation', 'spending', 'organization']
    },
    {
      id: '7',
      category: 'Budget Management',
      question: 'What happens if I go over budget in a category?',
      answer: 'If you exceed your budgeted amount in a category, the app will highlight this with visual indicators (typically red coloring). This helps you identify where you\'re overspending. You can either adjust your spending or modify your budget allocation for that category.',
      tags: ['over budget', 'overspending', 'exceed', 'budget limit', 'warning']
    },

    // Transaction Management
    {
      id: '8',
      category: 'Transaction Management',
      question: 'How do I edit or delete a transaction?',
      answer: 'To edit a transaction: Find the transaction in your Transactions list, click on it or the edit icon, modify the details, and save changes. To delete: Click the delete icon next to the transaction and confirm. Changes will immediately update your budget tracking.',
      tags: ['transaction', 'edit', 'delete', 'modify', 'remove']
    },
    {
      id: '9',
      category: 'Transaction Management',
      question: 'Can I filter my transactions?',
      answer: 'Yes! You can filter transactions by date range, category, transaction type (income/expense), and amount. Use the filter options at the top of the Transactions page. You can also search for specific transactions using keywords in the description.',
      tags: ['filter', 'search', 'transactions', 'date', 'category', 'type']
    },
    {
      id: '10',
      category: 'Transaction Management',
      question: 'What\'s the difference between income and expense transactions?',
      answer: 'Income transactions add money to your available funds (salary, freelance work, gifts, etc.). Expense transactions subtract money and are tracked against your budget categories (groceries, rent, entertainment, etc.). The dashboard shows your net income (income minus expenses).',
      tags: ['income', 'expense', 'difference', 'types', 'money', 'funds']
    },

    // Dashboard & Reports
    {
      id: '11',
      category: 'Dashboard & Reports',
      question: 'How do I read my dashboard?',
      answer: 'Your dashboard provides an overview of your finances: 1) Total income vs expenses for the current month, 2) Budget progress bars showing spending by category, 3) Recent transactions, 4) Budget status indicators. Green typically means you\'re on track, yellow means approaching limits, and red indicates overspending.',
      tags: ['dashboard', 'overview', 'reports', 'progress', 'status', 'indicators']
    },
    {
      id: '12',
      category: 'Dashboard & Reports',
      question: 'Can I see reports for previous months?',
      answer: 'Yes! You can view historical data by navigating to previous budgets and their associated transactions. Use the date filters in the Budgets and Transactions sections to view data from specific time periods. This helps you track your financial progress over time.',
      tags: ['reports', 'history', 'previous months', 'historical', 'past data']
    },

    // Account & Security
    {
      id: '13',
      category: 'Account & Security',
      question: 'How do I change my password?',
      answer: 'To change your password: 1) Go to Settings from the sidebar, 2) Look for the "Security" or "Password" section, 3) Enter your current password and new password, 4) Confirm the change. For security, use a strong password with letters, numbers, and special characters.',
      tags: ['password', 'change', 'security', 'settings', 'account']
    },
    {
      id: '14',
      category: 'Account & Security',
      question: 'I forgot my password. How do I reset it?',
      answer: 'On the login page, click "Forgot Password?" and enter your email address. You\'ll receive a password reset link via email. Click the link and follow the instructions to create a new password. Check your spam folder if you don\'t see the email within a few minutes.',
      tags: ['forgot password', 'reset', 'email', 'recovery', 'login']
    },
    {
      id: '15',
      category: 'Account & Security',
      question: 'Is my financial data secure?',
      answer: 'Yes! We use industry-standard security measures including: encrypted data transmission (HTTPS), secure password hashing, JWT authentication tokens, and secure database storage. We never store your banking login credentials and only track the financial data you manually enter.',
      tags: ['security', 'data protection', 'encryption', 'privacy', 'safe']
    },

    // Troubleshooting
    {
      id: '16',
      category: 'Troubleshooting',
      question: 'Why aren\'t my budget totals updating?',
      answer: 'If budget totals aren\'t updating: 1) Ensure transactions are assigned to the correct categories, 2) Check that transactions are within the budget\'s date range, 3) Refresh the page, 4) Verify the transaction was saved successfully. If issues persist, try logging out and back in.',
      tags: ['budget totals', 'not updating', 'sync', 'refresh', 'troubleshooting']
    },
    {
      id: '17',
      category: 'Troubleshooting',
      question: 'The app seems slow or unresponsive. What should I do?',
      answer: 'If the app is slow: 1) Check your internet connection, 2) Refresh the page, 3) Clear your browser cache, 4) Try a different browser, 5) Disable browser extensions temporarily. If using mobile, ensure you have adequate device storage and RAM available.',
      tags: ['slow', 'performance', 'unresponsive', 'browser', 'cache', 'speed']
    },
    {
      id: '18',
      category: 'Troubleshooting',
      question: 'I\'m having trouble logging in. What could be wrong?',
      answer: 'Login issues could be due to: 1) Incorrect email or password, 2) Unverified email address, 3) Browser cookies disabled, 4) Caps Lock enabled, 5) Temporary server issues. Try resetting your password or clearing browser data. Contact support if problems persist.',
      tags: ['login', 'authentication', 'access', 'password', 'email verification']
    },

    // Features & Tips
    {
      id: '19',
      category: 'Features & Tips',
      question: 'What are some best practices for budgeting?',
      answer: 'Budgeting best practices: 1) Follow the "every dollar has a job" principle - allocate all income to categories, 2) Review and adjust budgets monthly, 3) Track expenses regularly, 4) Set realistic budget amounts, 5) Include categories for savings and emergency funds, 6) Leave some buffer for unexpected expenses.',
      tags: ['best practices', 'tips', 'budgeting advice', 'money management', 'strategy']
    },
    {
      id: '20',
      category: 'Features & Tips',
      question: 'How often should I update my transactions?',
      answer: 'For best results, update transactions daily or weekly. Regular updates help you: 1) Stay aware of your spending, 2) Catch budget overruns early, 3) Maintain accurate financial records, 4) Make informed spending decisions. Set a reminder to review and add transactions at least weekly.',
      tags: ['frequency', 'updates', 'tracking', 'routine', 'habits', 'regular']
    }
  ];

  const categories = ['all', ...Array.from(new Set(faqData.map(item => item.category)))];

  const filteredFAQs = faqData.filter(item => {
    const matchesSearch = item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await support.sendContactEmail({
        email: contactForm.email,
        subject: contactForm.subject,
        message: contactForm.message
      });
      
      setFormSubmitted(true);
      setContactForm({ subject: '', message: '', email: '' });
    } catch (error: any) {
      console.error('Failed to send contact form:', error);
      setSubmitError(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Getting Started': return <InfoIcon />;
      case 'Budget Management': return <BudgetIcon />;
      case 'Transaction Management': return <TransactionIcon />;
      case 'Dashboard & Reports': return <DashboardIcon />;
      case 'Account & Security': return <SecurityIcon />;
      case 'Troubleshooting': return <WarningIcon />;
      case 'Features & Tips': return <CheckIcon />;
      default: return <HelpIcon />;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontWeight: 600,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
          }}
        >
          Support Center
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ 
            mb: { xs: 3, md: 4 },
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
            px: { xs: 1, sm: 0 }
          }}
        >
          Get help with your personal finance management
        </Typography>
        
        {/* Search Bar */}
        <Box sx={{ maxWidth: 600, mx: 'auto', mb: 3, px: { xs: 1, sm: 0 } }}>
          <TextField
            fullWidth
            placeholder="Search for help..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }
            }}
          />
          
          {/* Category Filter */}
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: { xs: 0.5, sm: 1 }, 
            justifyContent: 'center',
            px: { xs: 0.5, sm: 0 }
          }}>
            {categories.map((category) => (
              <Chip
                key={category}
                label={category === 'all' ? 'All Categories' : category}
                onClick={() => setSelectedCategory(category)}
                color={selectedCategory === category ? 'primary' : 'default'}
                variant={selectedCategory === category ? 'filled' : 'outlined'}
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  height: { xs: 28, sm: 32 },
                  '& .MuiChip-label': {
                    px: { xs: 1, sm: 1.5 }
                  }
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <Grid container spacing={{ xs: 2, md: 4 }}>
        {/* FAQ Section */}
        <Grid item xs={12} lg={8} order={{ xs: 2, lg: 1 }}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="h4" 
              gutterBottom 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2rem' },
                flexWrap: 'wrap'
              }}
            >
              <HelpIcon color="primary" />
              Frequently Asked Questions
            </Typography>
            
            {filteredFAQs.length === 0 ? (
              <Alert severity="info" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                No FAQs found matching your search criteria. Try different keywords or select a different category.
              </Alert>
            ) : (
              <Box>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 3,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}
                >
                  Showing {filteredFAQs.length} of {faqData.length} questions
                </Typography>
                
                {filteredFAQs.map((faq) => (
                  <Accordion 
                    key={faq.id} 
                    sx={{ 
                      mb: 1,
                      '&:before': {
                        display: 'none'
                      }
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        '& .MuiAccordionSummary-content': {
                          margin: { xs: '8px 0', sm: '12px 0' }
                        }
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: { xs: 'flex-start', sm: 'center' }, 
                        gap: { xs: 1, sm: 2 }, 
                        width: '100%',
                        flexDirection: { xs: 'column', sm: 'row' }
                      }}>
                        <Box sx={{ 
                          display: { xs: 'none', sm: 'block' },
                          minWidth: 'auto'
                        }}>
                          {getCategoryIcon(faq.category)}
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 500,
                              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                              lineHeight: 1.3,
                              mb: { xs: 0.5, sm: 0 }
                            }}
                          >
                            {faq.question}
                          </Typography>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            mt: { xs: 0.5, sm: 0.5 }
                          }}>
                            <Box sx={{ 
                              display: { xs: 'block', sm: 'none' },
                              minWidth: 'auto'
                            }}>
                              {getCategoryIcon(faq.category)}
                            </Box>
                            <Chip 
                              label={faq.category} 
                              size="small" 
                              variant="outlined"
                              sx={{ 
                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                height: { xs: 20, sm: 24 }
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: { xs: 1, sm: 2 } }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          lineHeight: 1.6, 
                          mb: 2,
                          fontSize: { xs: '0.9rem', sm: '1rem' }
                        }}
                      >
                        {faq.answer}
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: { xs: 0.25, sm: 0.5 }
                      }}>
                        {faq.tags.map((tag) => (
                          <Chip 
                            key={tag} 
                            label={tag} 
                            size="small" 
                            variant="outlined"
                            sx={{ 
                              fontSize: { xs: '0.6rem', sm: '0.7rem' },
                              height: { xs: 18, sm: 20 }
                            }}
                          />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4} order={{ xs: 1, lg: 2 }}>
          {/* Quick Help */}
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              Quick Help
            </Typography>
            <List dense>
              <ListItem sx={{ px: { xs: 0, sm: 2 } }}>
                <ListItemIcon sx={{ minWidth: { xs: 36, sm: 40 } }}>
                  <CheckIcon color="success" />
                </ListItemIcon>
                <ListItemText 
                  primary="Getting Started Guide" 
                  secondary="Learn the basics of using the app"
                  primaryTypographyProps={{
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }}
                  secondaryTypographyProps={{
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}
                />
              </ListItem>
              <ListItem sx={{ px: { xs: 0, sm: 2 } }}>
                <ListItemIcon sx={{ minWidth: { xs: 36, sm: 40 } }}>
                  <BudgetIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Budget Best Practices" 
                  secondary="Tips for effective budgeting"
                  primaryTypographyProps={{
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }}
                  secondaryTypographyProps={{
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}
                />
              </ListItem>
              <ListItem sx={{ px: { xs: 0, sm: 2 } }}>
                <ListItemIcon sx={{ minWidth: { xs: 36, sm: 40 } }}>
                  <SecurityIcon color="warning" />
                </ListItemIcon>
                <ListItemText 
                  primary="Security & Privacy" 
                  secondary="How we protect your data"
                  primaryTypographyProps={{
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }}
                  secondaryTypographyProps={{
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}
                />
              </ListItem>
            </List>
          </Paper>

          {/* Contact Support */}
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              Contact Support
            </Typography>
            
            {formSubmitted ? (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 2,
                  fontSize: { xs: '0.85rem', sm: '0.9rem' }
                }}
              >
                Thank you for contacting us! We'll get back to you within 24 hours.
              </Alert>
            ) : (
              <>
                {submitError && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 2,
                      fontSize: { xs: '0.85rem', sm: '0.9rem' },
                      '& .MuiAlert-message': {
                        whiteSpace: 'pre-line' // Allow line breaks in error messages
                      }
                    }}
                  >
                    {submitError}
                  </Alert>
                )}
                <Box component="form" onSubmit={handleContactSubmit}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                  required
                  sx={{ 
                    mb: 2,
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Subject"
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                  required
                  helperText="Subject must be at least 5 characters"
                  sx={{ 
                    mb: 2,
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Message"
                  multiline
                  rows={4}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                  required
                  helperText="Message must be at least 10 characters"
                  sx={{ 
                    mb: 2,
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    },
                    '& .MuiInputBase-root': {
                      '& textarea': {
                        minHeight: { xs: '60px', sm: '80px' }
                      }
                    }
                  }}
                />
                <Button 
                  type="submit" 
                  variant="contained" 
                  fullWidth
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={20} /> : <EmailIcon />}
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    py: { xs: 1, sm: 1.5 }
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </Box>
              </>
            )}

            <Divider sx={{ my: 2 }} />
            
            <Typography 
              variant="subtitle2" 
              gutterBottom
              sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
            >
              Other ways to reach us:
            </Typography>
            <List dense>
              <ListItem sx={{ px: { xs: 0, sm: 2 } }}>
                <ListItemIcon sx={{ minWidth: { xs: 36, sm: 40 } }}>
                  <EmailIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="support@personalfinance.app" 
                  primaryTypographyProps={{
                    fontSize: { xs: '0.85rem', sm: '0.9rem' }
                  }}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Support; 